// src/cockpit4/components/WorkflowNodeEditor.tsx
// ============================================
// 可视化流程编辑器增强（工作流中心 P0-功能2）
//
// 功能：
//   1. 拖拽式节点编辑（左侧节点面板拖拽到画布生成节点）
//   2. 丰富的节点类型（9 种）：AI调用/数据处理/条件分支/循环/人工审批/通知/等待/HTTP请求/脚本
//   3. 节点配置面板（右侧抽屉，按节点类型显示不同配置项）
//   4. 实时预览和测试（单节点本地模拟测试运行）
//   5. 版本管理和回滚（每次保存生成版本快照，可回滚到任意历史版本）
//   6. 复制/粘贴/撤销/重做（基于内存快照栈 + 剪贴板）
//   7. 节点分组和注释（右键菜单设置分组/注释，分组以虚线框聚合）
//   8. 节点连线（SVG 贝塞尔曲线 + 连接点拖拽生成边）
//   9. 画布平移与缩放（原生鼠标事件实现）
//
// 数据来源：
//   - localStorage 持久化（key: xeaos_workflow_drafts）
//   - 无草稿时显示空白画布，由用户主动创建草稿
//
// 设计原则：
//   - 不引入 ReactFlow 等新依赖，纯 SVG + div 实现节点连线和拖拽
//   - 真实数据驱动：所有节点/边均由用户编辑产生并持久化到 localStorage
//   - 深色科技风：玻璃拟态、border-white/[0.06]、bg-white/[0.02]
// ============================================

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cpu,
  Database,
  GitBranch,
  Repeat,
  CheckCircle,
  Bell,
  Clock,
  Globe,
  Code,
  Undo2,
  Redo2,
  Save,
  Play,
  History,
  Plus,
  Trash2,
  Copy,
  ClipboardPaste,
  X,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Pencil,
  FileText,
  Settings,
  Folder,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Workflow,
  Layers,
  type LucideIcon,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 节点类型 */
export type NodeType =
  | "ai_call"
  | "data_process"
  | "condition"
  | "loop"
  | "approval"
  | "notification"
  | "wait"
  | "http"
  | "script";

/** 节点配置（不同类型使用不同字段） */
export interface WorkflowNodeConfig {
  // AI 调用 / 数据处理 / 循环
  runtime?: string;
  action?: string;
  params?: Record<string, unknown>;
  // 条件分支
  conditions?: { field: string; operator: string; value: string }[];
  // 审批
  approvers?: string[];
  approvalType?: "any" | "all" | "sequential";
  // 通知
  channel?: string;
  message?: string;
  // HTTP
  url?: string;
  method?: string;
  // 等待
  duration?: number;
  // 脚本
  script?: string;
}

/** 流程节点 */
export interface WorkflowNode {
  id: string;
  type: NodeType;
  label: string;
  /** 画布坐标（未缩放） */
  x: number;
  y: number;
  config: WorkflowNodeConfig;
  /** 分组名 */
  group?: string;
  /** 注释 */
  note?: string;
}

/** 流程连线 */
export interface WorkflowEdge {
  id: string;
  /** 源节点 ID */
  from: string;
  /** 目标节点 ID */
  to: string;
  /** 连线标签（条件分支用） */
  label?: string;
}

/** 版本快照 */
export interface VersionSnapshot {
  version: number;
  /** {nodes, edges} 的 JSON 字符串 */
  snapshot: string;
  timestamp: string;
}

/** 工作流草稿 */
export interface WorkflowDraft {
  id: string;
  name: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  version: number;
  history: VersionSnapshot[];
  createdAt: string;
  updatedAt: string;
}

/** 节点测试结果 */
interface TestResult {
  nodeId: string;
  status: "success" | "warning" | "error";
  durationMs: number;
  logs: string[];
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_workflow_drafts";

/** 节点尺寸（画布坐标） */
const NODE_W = 168;
const NODE_H = 60;

/** 节点类型元数据 */
const NODE_TYPE_META: Record<
  NodeType,
  { label: string; color: string; icon: LucideIcon }
> = {
  ai_call: { label: "AI调用", color: "#A855F7", icon: Cpu },
  data_process: { label: "数据处理", color: "#3B82F6", icon: Database },
  condition: { label: "条件分支", color: "#F59E0B", icon: GitBranch },
  loop: { label: "循环", color: "#06B6D4", icon: Repeat },
  approval: { label: "人工审批", color: "#EF4444", icon: CheckCircle },
  notification: { label: "通知", color: "#10B981", icon: Bell },
  wait: { label: "等待", color: "#6B7280", icon: Clock },
  http: { label: "HTTP请求", color: "#8B5CF6", icon: Globe },
  script: { label: "脚本", color: "#EC4899", icon: Code },
};

/** 节点类型顺序（左侧面板展示顺序） */
const NODE_TYPE_ORDER: NodeType[] = [
  "ai_call",
  "data_process",
  "condition",
  "loop",
  "approval",
  "notification",
  "wait",
  "http",
  "script",
];

/** 审批策略选项 */
const APPROVAL_TYPES: { value: WorkflowNodeConfig["approvalType"]; label: string }[] = [
  { value: "any", label: "任一审批通过" },
  { value: "all", label: "全部审批通过" },
  { value: "sequential", label: "顺序审批" },
];

/** 通知渠道选项 */
const NOTIFY_CHANNELS = [
  { value: "im", label: "即时通讯" },
  { value: "email", label: "邮件" },
  { value: "sms", label: "短信" },
  { value: "webhook", label: "Webhook" },
];

/** HTTP 方法选项 */
const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

/** 条件运算符选项 */
const CONDITION_OPERATORS = ["==", "!=", ">", "<", ">=", "<=", "contains", "in"];

// ============================================
// localStorage 持久化
// ============================================

/** 加载草稿列表 */
function loadDrafts(): WorkflowDraft[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as WorkflowDraft[];
    if (!Array.isArray(list)) return [];
    return list;
  } catch {
    return [];
  }
}

/** 写入草稿列表 */
function persistDrafts(drafts: WorkflowDraft[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  } catch {
    // 配额不足或隐私模式，静默降级
  }
}

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function uid(prefix = "n"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}${Date.now().toString(36).slice(-4)}`;
}

/** 生成新节点（默认配置） */
function createNode(type: NodeType, x: number, y: number): WorkflowNode {
  const meta = NODE_TYPE_META[type];
  const node: WorkflowNode = {
    id: uid("node"),
    type,
    label: meta.label,
    x: Math.round(x),
    y: Math.round(y),
    config: {},
  };
  // 按类型初始化默认配置
  switch (type) {
    case "ai_call":
      node.config = { runtime: "doubao", action: "", params: {} };
      break;
    case "data_process":
      node.config = { action: "transform", params: {} };
      break;
    case "condition":
      node.config = { conditions: [{ field: "", operator: "==", value: "" }] };
      break;
    case "loop":
      node.config = { action: "iterate", params: { maxIterations: 10 } };
      break;
    case "approval":
      node.config = { approvers: [], approvalType: "any" };
      break;
    case "notification":
      node.config = { channel: "im", message: "" };
      break;
    case "http":
      node.config = { url: "", method: "GET" };
      break;
    case "wait":
      node.config = { duration: 60 };
      break;
    case "script":
      node.config = { script: "" };
      break;
  }
  return node;
}

/** 节点右锚点（连线起点） */
function rightAnchor(n: WorkflowNode): { x: number; y: number } {
  return { x: n.x + NODE_W, y: n.y + NODE_H / 2 };
}

/** 节点左锚点（连线终点） */
function leftAnchor(n: WorkflowNode): { x: number; y: number } {
  return { x: n.x, y: n.y + NODE_H / 2 };
}

/** 生成贝塞尔曲线路径 */
function bezierPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number
): string {
  const dx = Math.max(40, Math.abs(tx - sx) * 0.5);
  return `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`;
}

/** 工作流状态快照（用于撤销/重做） */
interface Snapshot {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/** 深拷贝快照 */
function cloneSnapshot(s: Snapshot): Snapshot {
  return {
    nodes: s.nodes.map((n) => ({ ...n, config: { ...n.config } })),
    edges: s.edges.map((e) => ({ ...e })),
  };
}

/** 计算分组包围盒 */
function computeGroupBounds(
  nodes: WorkflowNode[]
): Map<string, { minX: number; minY: number; maxX: number; maxY: number }> {
  const map = new Map<string, { minX: number; minY: number; maxX: number; maxY: number }>();
  nodes.forEach((n) => {
    if (!n.group) return;
    const cur = map.get(n.group) || {
      minX: n.x,
      minY: n.y,
      maxX: n.x + NODE_W,
      maxY: n.y + NODE_H,
    };
    cur.minX = Math.min(cur.minX, n.x);
    cur.minY = Math.min(cur.minY, n.y);
    cur.maxX = Math.max(cur.maxX, n.x + NODE_W);
    cur.maxY = Math.max(cur.maxY, n.y + NODE_H);
    map.set(n.group, cur);
  });
  return map;
}

// ============================================
// 当前交互状态（ref，避免闭包陈旧）
// ============================================

type Interaction =
  | { kind: "newNode"; type: NodeType; clientX: number; clientY: number; moved: boolean }
  | {
      kind: "moveNode";
      id: string;
      startNodeX: number;
      startNodeY: number;
      startClientX: number;
      startClientY: number;
      moved: boolean;
    }
  | {
      kind: "connect";
      fromId: string;
      fromX: number;
      fromY: number;
      clientX: number;
      clientY: number;
    }
  | {
      kind: "pan";
      startPanX: number;
      startPanY: number;
      startClientX: number;
      startClientY: number;
    };

/** 当前焦点是否在文本输入元素上（用于键盘快捷键判断） */
function isEditingText(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
}

// ============================================
// 主组件
// ============================================

export default function WorkflowNodeEditor() {
  // ---- 草稿与工作区状态 ----
  const [drafts, setDrafts] = useState<WorkflowDraft[]>([]);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [nodes, setNodes] = useState<WorkflowNode[]>([]);
  const [edges, setEdges] = useState<WorkflowEdge[]>([]);
  const [loaded, setLoaded] = useState(false);

  // ---- 选区与编辑状态 ----
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string;
  } | null>(null);

  // ---- 视口（平移/缩放） ----
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [zoom, setZoom] = useState(1);

  // ---- 撤销/重做栈 ----
  const [undoStack, setUndoStack] = useState<Snapshot[]>([]);
  const [redoStack, setRedoStack] = useState<Snapshot[]>([]);
  const editingFieldRef = useRef<string | null>(null);

  // ---- 剪贴板 ----
  const [clipboardNode, setClipboardNode] = useState<WorkflowNode | null>(null);

  // ---- 测试运行 ----
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // ---- 版本面板 ----
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  // ---- 拖拽临时视觉状态 ----
  const [ghost, setGhost] = useState<{ type: NodeType; x: number; y: number } | null>(null);
  const [tempEdge, setTempEdge] = useState<{
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
    color: string;
  } | null>(null);

  // ---- refs ----
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const interactionRef = useRef<Interaction | null>(null);
  const viewportRef = useRef({ panX, panY, zoom });
  const nodesRef = useRef<WorkflowNode[]>(nodes);
  const edgesRef = useRef<WorkflowEdge[]>(edges);
  const currentDraftIdRef = useRef<string | null>(currentDraftId);
  const skipAutosaveRef = useRef(false);
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState<string>("");

  // 同步 refs
  useEffect(() => {
    viewportRef.current = { panX, panY, zoom };
  }, [panX, panY, zoom]);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);
  useEffect(() => {
    currentDraftIdRef.current = currentDraftId;
  }, [currentDraftId]);

  // ============================================
  // 初始加载草稿
  // ============================================
  useEffect(() => {
    const list = loadDrafts();
    setDrafts(list);
    setLoaded(true);
  }, []);

  // 注入输入框统一样式（仅一次，使用专属类名避免全局污染）
  useEffect(() => {
    if (document.getElementById("workflow-editor-input-style")) return;
    const styleEl = document.createElement("style");
    styleEl.id = "workflow-editor-input-style";
    styleEl.textContent = WFE_INPUT_CSS;
    document.head.appendChild(styleEl);
  }, []);

  // 持久化草稿（drafts 变化时自动写入）
  useEffect(() => {
    if (!loaded) return;
    persistDrafts(drafts);
  }, [drafts, loaded]);

  // 自动保存工作区到当前草稿（去版本化，防丢失）
  useEffect(() => {
    if (!loaded || !currentDraftId || skipAutosaveRef.current) return;
    const id = currentDraftId;
    const handle = setTimeout(() => {
      setDrafts((prev) =>
        prev.map((d) =>
          d.id === id
            ? {
                ...d,
                nodes: nodesRef.current,
                edges: edgesRef.current,
                updatedAt: new Date().toISOString(),
              }
            : d
        )
      );
    }, 600);
    return () => clearTimeout(handle);
  }, [nodes, edges, currentDraftId, loaded]);

  // 脏标记：是否有未保存到版本的更改
  const dirty = useMemo(() => {
    if (!currentDraftId) return false;
    const snap = JSON.stringify({ nodes, edges });
    return snap !== lastSavedSnapshot;
  }, [nodes, edges, currentDraftId, lastSavedSnapshot]);

  // 当前草稿对象
  const currentDraft = useMemo(
    () => drafts.find((d) => d.id === currentDraftId) || null,
    [drafts, currentDraftId]
  );

  // ============================================
  // 撤销/重做核心
  // ============================================

  /** 在变更前调用：把当前状态压入撤销栈，清空重做栈 */
  const pushHistory = useCallback(() => {
    setUndoStack((prev) => [
      ...prev,
      cloneSnapshot({ nodes: nodesRef.current, edges: edgesRef.current }),
    ]);
    setRedoStack([]);
  }, []);

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedoStack((r) => [
        ...r,
        cloneSnapshot({ nodes: nodesRef.current, edges: edgesRef.current }),
      ]);
      skipAutosaveRef.current = true;
      setNodes(last.nodes);
      setEdges(last.edges);
      skipAutosaveRef.current = false;
      return prev.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev[prev.length - 1];
      setUndoStack((u) => [
        ...u,
        cloneSnapshot({ nodes: nodesRef.current, edges: edgesRef.current }),
      ]);
      skipAutosaveRef.current = true;
      setNodes(next.nodes);
      setEdges(next.edges);
      skipAutosaveRef.current = false;
      return prev.slice(0, -1);
    });
  }, []);

  // ============================================
  // 草稿管理
  // ============================================

  /** 切换草稿前先冲刷当前工作区 */
  const flushCurrent = useCallback(() => {
    const id = currentDraftIdRef.current;
    if (!id) return;
    setDrafts((prev) =>
      prev.map((d) =>
        d.id === id
          ? {
              ...d,
              nodes: nodesRef.current,
              edges: edgesRef.current,
              updatedAt: new Date().toISOString(),
            }
          : d
      )
    );
  }, []);

  /** 新建草稿 */
  const handleCreateDraft = useCallback(() => {
    flushCurrent();
    const now = new Date().toISOString();
    const id = uid("draft");
    const emptySnap = JSON.stringify({ nodes: [], edges: [] });
    const draft: WorkflowDraft = {
      id,
      name: `工作流 ${new Date().toLocaleString("zh-CN", { hour12: false }).slice(5, 16)}`,
      nodes: [],
      edges: [],
      version: 1,
      history: [{ version: 1, snapshot: emptySnap, timestamp: now }],
      createdAt: now,
      updatedAt: now,
    };
    setDrafts((prev) => [...prev, draft]);
    // 选中并载入
    skipAutosaveRef.current = true;
    setCurrentDraftId(id);
    setNodes([]);
    setEdges([]);
    setUndoStack([]);
    setRedoStack([]);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setTestResult(null);
    setPanX(0);
    setPanY(0);
    setZoom(1);
    setLastSavedSnapshot(emptySnap);
    skipAutosaveRef.current = false;
  }, [flushCurrent]);

  /** 载入选定草稿到工作区 */
  const handleSelectDraft = useCallback(
    (id: string) => {
      if (id === currentDraftIdRef.current) return;
      flushCurrent();
      const draft = drafts.find((d) => d.id === id);
      if (!draft) return;
      skipAutosaveRef.current = true;
      setCurrentDraftId(id);
      setNodes(draft.nodes);
      setEdges(draft.edges);
      setUndoStack([]);
      setRedoStack([]);
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setTestResult(null);
      setPanX(0);
      setPanY(0);
      setZoom(1);
      setLastSavedSnapshot(
        JSON.stringify({
          nodes: draft.nodes,
          edges: draft.edges,
        })
      );
      skipAutosaveRef.current = false;
    },
    [drafts, flushCurrent]
  );

  /** 删除草稿 */
  const handleDeleteDraft = useCallback(
    (id: string) => {
      setDrafts((prev) => prev.filter((d) => d.id !== id));
      if (currentDraftIdRef.current === id) {
        skipAutosaveRef.current = true;
        setCurrentDraftId(null);
        setNodes([]);
        setEdges([]);
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
        setTestResult(null);
        setUndoStack([]);
        setRedoStack([]);
        setLastSavedSnapshot("");
        skipAutosaveRef.current = false;
      }
    },
    []
  );

  /** 重命名草稿 */
  const handleRenameDraft = useCallback((id: string, name: string) => {
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, name, updatedAt: new Date().toISOString() } : d))
    );
  }, []);

  /** 保存草稿（生成新版本快照） */
  const handleSave = useCallback(() => {
    if (!currentDraftId) return;
    const now = new Date().toISOString();
    setDrafts((prev) =>
      prev.map((d) => {
        if (d.id !== currentDraftId) return d;
        const nextVersion = d.version + 1;
        const snap = JSON.stringify({ nodes: nodesRef.current, edges: edgesRef.current });
        return {
          ...d,
          nodes: nodesRef.current,
          edges: edgesRef.current,
          version: nextVersion,
          history: [
            ...d.history,
            { version: nextVersion, snapshot: snap, timestamp: now },
          ],
          updatedAt: now,
        };
      })
    );
    setLastSavedSnapshot(
      JSON.stringify({
        nodes: nodesRef.current,
        edges: edgesRef.current,
      })
    );
  }, [currentDraftId]);

  /** 回滚到指定版本快照 */
  const handleRollback = useCallback(
    (version: number) => {
      if (!currentDraft) return;
      const entry = currentDraft.history.find((h) => h.version === version);
      if (!entry) return;
      try {
        const parsed = JSON.parse(entry.snapshot) as {
          nodes: WorkflowNode[];
          edges: WorkflowEdge[];
        };
        pushHistory();
        skipAutosaveRef.current = true;
        setNodes(parsed.nodes);
        setEdges(parsed.edges);
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
        skipAutosaveRef.current = false;
        setShowVersionPanel(false);
      } catch {
        // 快照损坏，忽略
      }
    },
    [currentDraft, pushHistory]
  );

  // ============================================
  // 节点 / 边 操作
  // ============================================

  /** 客户端坐标 → 画布坐标 */
  const clientToCanvas = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    const vp = viewportRef.current;
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (clientX - rect.left - vp.panX) / vp.zoom,
      y: (clientY - rect.top - vp.panY) / vp.zoom,
    };
  }, []);

  /** 添加新节点（来自左侧面板拖拽） */
  const addNodeAt = useCallback(
    (type: NodeType, canvasX: number, canvasY: number) => {
      if (!currentDraftId) return;
      pushHistory();
      const node = createNode(type, canvasX - NODE_W / 2, canvasY - NODE_H / 2);
      setNodes((prev) => [...prev, node]);
      setSelectedNodeId(node.id);
      setSelectedEdgeId(null);
    },
    [currentDraftId, pushHistory]
  );

  /** 更新节点 */
  const updateNode = useCallback((id: string, patch: Partial<WorkflowNode>) => {
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  }, []);

  /** 更新节点配置 */
  const updateNodeConfig = useCallback(
    (id: string, patch: Partial<WorkflowNodeConfig>) => {
      setNodes((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, config: { ...n.config, ...patch } } : n
        )
      );
    },
    []
  );

  /** 删除节点（连同相关边） */
  const deleteNode = useCallback(
    (id: string) => {
      pushHistory();
      setNodes((prev) => prev.filter((n) => n.id !== id));
      setEdges((prev) => prev.filter((e) => e.from !== id && e.to !== id));
      if (selectedNodeId === id) setSelectedNodeId(null);
    },
    [pushHistory, selectedNodeId]
  );

  /** 删除边 */
  const deleteEdge = useCallback(
    (id: string) => {
      pushHistory();
      setEdges((prev) => prev.filter((e) => e.id !== id));
      if (selectedEdgeId === id) setSelectedEdgeId(null);
    },
    [pushHistory, selectedEdgeId]
  );

  /** 创建边（去重、防自环） */
  const createEdge = useCallback(
    (fromId: string, toId: string) => {
      if (fromId === toId) return;
      const exists = edgesRef.current.some(
        (e) => e.from === fromId && e.to === toId
      );
      if (exists) return;
      pushHistory();
      const edge: WorkflowEdge = {
        id: uid("edge"),
        from: fromId,
        to: toId,
      };
      setEdges((prev) => [...prev, edge]);
    },
    [pushHistory]
  );

  /** 更新边标签 */
  const updateEdgeLabel = useCallback((id: string, label: string) => {
    setEdges((prev) => prev.map((e) => (e.id === id ? { ...e, label } : e)));
  }, []);

  /** 复制节点到剪贴板 */
  const copyNode = useCallback((id: string) => {
    const n = nodesRef.current.find((x) => x.id === id);
    if (n) setClipboardNode({ ...n, config: { ...n.config } });
  }, []);

  /** 粘贴节点 */
  const pasteNode = useCallback(() => {
    if (!clipboardNode || !currentDraftId) return;
    pushHistory();
    const copy: WorkflowNode = {
      ...clipboardNode,
      id: uid("node"),
      label: `${clipboardNode.label} 副本`,
      x: clipboardNode.x + 32,
      y: clipboardNode.y + 32,
      config: { ...clipboardNode.config },
    };
    setNodes((prev) => [...prev, copy]);
    setSelectedNodeId(copy.id);
  }, [clipboardNode, currentDraftId, pushHistory]);

  /** 设置节点分组 */
  const setNodeGroup = useCallback((id: string, group: string) => {
    setNodes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, group: group || undefined } : n))
    );
  }, []);

  // ============================================
  // 全局鼠标事件（拖拽 / 连线 / 平移）
  // ============================================

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const it = interactionRef.current;
      if (!it) return;
      if (it.kind === "newNode") {
        if (!it.moved && Math.abs(e.clientX - it.clientX) + Math.abs(e.clientY - it.clientY) < 3)
          return;
        it.moved = true;
        setGhost({ type: it.type, x: e.clientX, y: e.clientY });
        return;
      }
      if (it.kind === "moveNode") {
        const vp = viewportRef.current;
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const dx = (e.clientX - it.startClientX) / vp.zoom;
        const dy = (e.clientY - it.startClientY) / vp.zoom;
        if (!it.moved && Math.abs(dx) + Math.abs(dy) < 2) return;
        if (!it.moved) {
          it.moved = true;
          // 拖动开始时压栈一次
          setUndoStack((prev) => [
            ...prev,
            cloneSnapshot({ nodes: nodesRef.current, edges: edgesRef.current }),
          ]);
          setRedoStack([]);
        }
        const nx = it.startNodeX + dx;
        const ny = it.startNodeY + dy;
        setNodes((prev) =>
          prev.map((n) => (n.id === it.id ? { ...n, x: Math.round(nx), y: Math.round(ny) } : n))
        );
        return;
      }
      if (it.kind === "connect") {
        const c = clientToCanvas(e.clientX, e.clientY);
        it.clientX = e.clientX;
        it.clientY = e.clientY;
        const fromNode = nodesRef.current.find((n) => n.id === it.fromId);
        const color = fromNode ? NODE_TYPE_META[fromNode.type].color : "#888";
        setTempEdge({ fromX: it.fromX, fromY: it.fromY, toX: c.x, toY: c.y, color });
        return;
      }
      if (it.kind === "pan") {
        const dx = e.clientX - it.startClientX;
        const dy = e.clientY - it.startClientY;
        setPanX(it.startPanX + dx);
        setPanY(it.startPanY + dy);
        return;
      }
    };

    const onUp = (e: MouseEvent) => {
      const it = interactionRef.current;
      if (!it) {
        interactionRef.current = null;
        setGhost(null);
        setTempEdge(null);
        return;
      }
      if (it.kind === "newNode") {
        if (it.moved) {
          // 判断是否落在画布内
          const rect = canvasRef.current?.getBoundingClientRect();
          if (
            rect &&
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
          ) {
            const c = clientToCanvas(e.clientX, e.clientY);
            addNodeAt(it.type, c.x, c.y);
          }
        }
        setGhost(null);
      } else if (it.kind === "connect") {
        // 命中检测：是否落在某节点上
        const target = document
          .elementFromPoint(e.clientX, e.clientY)
          ?.closest("[data-node-id]") as HTMLElement | null;
        const toId = target?.getAttribute("data-node-id") || null;
        if (toId && toId !== it.fromId) {
          createEdge(it.fromId, toId);
        }
        setTempEdge(null);
      }
      interactionRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [addNodeAt, clientToCanvas, createEdge]);

  // ============================================
  // 画布交互处理
  // ============================================

  /** 左侧节点卡片：按下开始"新建节点"拖拽 */
  const handleTypeCardMouseDown = (e: ReactMouseEvent, type: NodeType) => {
    if (!currentDraftId) return;
    e.preventDefault();
    interactionRef.current = {
      kind: "newNode",
      type,
      clientX: e.clientX,
      clientY: e.clientY,
      moved: false,
    };
  };

  /** 画布背景：按下开始平移 */
  const handleCanvasMouseDown = (e: ReactMouseEvent) => {
    // 仅背景点击触发平移（节点/边已 stopPropagation）
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setContextMenu(null);
    interactionRef.current = {
      kind: "pan",
      startPanX: panX,
      startPanY: panY,
      startClientX: e.clientX,
      startClientY: e.clientY,
    };
  };

  /** 节点：按下开始移动 */
  const handleNodeMouseDown = (e: ReactMouseEvent, node: WorkflowNode) => {
    e.stopPropagation();
    if (editingLabelId === node.id) return;
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
    interactionRef.current = {
      kind: "moveNode",
      id: node.id,
      startNodeX: node.x,
      startNodeY: node.y,
      startClientX: e.clientX,
      startClientY: e.clientY,
      moved: false,
    };
  };

  /** 连接点：按下开始连线 */
  const handleConnectMouseDown = (e: ReactMouseEvent, node: WorkflowNode) => {
    e.stopPropagation();
    const a = rightAnchor(node);
    interactionRef.current = {
      kind: "connect",
      fromId: node.id,
      fromX: a.x,
      fromY: a.y,
      clientX: e.clientX,
      clientY: e.clientY,
    };
  };

  /** 右键节点菜单 */
  const handleNodeContextMenu = (e: ReactMouseEvent, node: WorkflowNode) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedNodeId(node.id);
    setContextMenu({ x: e.clientX, y: e.clientY, nodeId: node.id });
  };

  // 缩放
  const zoomBy = (factor: number) => {
    setZoom((z) => Math.min(2, Math.max(0.3, +(z * factor).toFixed(2))));
  };
  const resetView = () => {
    setPanX(0);
    setPanY(0);
    setZoom(1);
  };

  // ============================================
  // 键盘快捷键
  // ============================================

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      // 编辑文本中不拦截
      if (isEditingText()) return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && !e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
        return;
      }
      if (ctrl && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) {
        e.preventDefault();
        redo();
        return;
      }
      if (ctrl && e.key.toLowerCase() === "c" && selectedNodeId) {
        e.preventDefault();
        copyNode(selectedNodeId);
        return;
      }
      if (ctrl && e.key.toLowerCase() === "v") {
        e.preventDefault();
        pasteNode();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace")) {
        if (selectedNodeId) {
          e.preventDefault();
          deleteNode(selectedNodeId);
        } else if (selectedEdgeId) {
          e.preventDefault();
          deleteEdge(selectedEdgeId);
        }
        return;
      }
      if (e.key === "Escape") {
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
        setContextMenu(null);
        setEditingLabelId(null);
        setShowVersionPanel(false);
      }
    },
    [
      undo,
      redo,
      selectedNodeId,
      selectedEdgeId,
      copyNode,
      pasteNode,
      deleteNode,
      deleteEdge,
    ]
  );

  // ============================================
  // 单节点测试（本地模拟）
  // ============================================

  const runNodeTest = useCallback(async (node: WorkflowNode) => {
    setTesting(true);
    setTestResult(null);
    const start = performance.now();
    // 模拟执行延迟
    await new Promise((r) => setTimeout(r, 420 + Math.random() * 380));
    const logs: string[] = [];
    let status: TestResult["status"] = "success";
    const c = node.config;

    const fail = (msg: string) => {
      status = "error";
      logs.push(`✗ ${msg}`);
    };
    const warn = (msg: string) => {
      if (status !== "error") status = "warning";
      logs.push(`! ${msg}`);
    };

    switch (node.type) {
      case "ai_call":
        logs.push(`运行时: ${c.runtime || "未指定"}`);
        logs.push(`动作: ${c.action || "未指定"}`);
        if (!c.action) fail("缺少 action 动作名称");
        if (c.params && Object.keys(c.params).length)
          logs.push(`参数: ${JSON.stringify(c.params)}`);
        if (status === "success") logs.push("✓ AI 调用模拟完成，返回 OK");
        break;
      case "data_process":
        logs.push(`动作: ${c.action || "transform"}`);
        if (c.params && Object.keys(c.params).length)
          logs.push(`参数: ${JSON.stringify(c.params)}`);
        logs.push("✓ 数据处理模拟完成");
        break;
      case "condition":
        if (!c.conditions || c.conditions.length === 0) {
          fail("未配置任何条件");
        } else {
          c.conditions.forEach((cond, i) => {
            logs.push(
              `条件${i + 1}: ${cond.field || "?"} ${cond.operator} ${cond.value || "?"}`
            );
            if (!cond.field) warn(`条件${i + 1} 缺少字段名`);
          });
          if (status === "success") logs.push("✓ 条件分支就绪（运行时按输入求值）");
        }
        break;
      case "loop":
        logs.push(`动作: ${c.action || "iterate"}`);
        const max = (c.params as { maxIterations?: number })?.maxIterations;
        logs.push(`最大迭代: ${max ?? "未限制"}`);
        logs.push("✓ 循环模拟完成");
        break;
      case "approval":
        if (!c.approvers || c.approvers.length === 0) {
          warn("未指定审批人");
        } else {
          logs.push(`审批人: ${c.approvers.join(", ")}`);
        }
        logs.push(`策略: ${c.approvalType || "any"}`);
        if (status === "success") logs.push("✓ 审批节点就绪");
        break;
      case "notification":
        logs.push(`渠道: ${c.channel || "未指定"}`);
        logs.push(`消息: ${c.message ? c.message.slice(0, 40) + (c.message.length > 40 ? "…" : "") : "空"}`);
        if (!c.message) warn("消息内容为空");
        if (status === "success") logs.push("✓ 通知模拟发送完成");
        break;
      case "http":
        logs.push(`方法: ${c.method || "GET"}`);
        logs.push(`URL: ${c.url || "未指定"}`);
        if (!c.url) {
          fail("缺少 URL");
        } else {
          try {
            new URL(c.url);
          } catch {
            fail("URL 格式非法");
          }
        }
        if (status === "success") logs.push("✓ HTTP 请求模拟完成，状态 200");
        break;
      case "wait":
        logs.push(`等待时长: ${c.duration ?? 0} 秒`);
        if ((c.duration ?? 0) <= 0) warn("等待时长非正数");
        if (status === "success") logs.push("✓ 等待节点就绪");
        break;
      case "script":
        if (!c.script) {
          fail("脚本内容为空");
        } else {
          logs.push(`脚本长度: ${c.script.length} 字符`);
          logs.push("✓ 脚本模拟执行完成");
        }
        break;
    }

    setTestResult({
      nodeId: node.id,
      status,
      durationMs: Math.round(performance.now() - start),
      logs,
    });
    setTesting(false);
  }, []);

  // ============================================
  // 派生数据
  // ============================================

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );
  const selectedEdge = useMemo(
    () => edges.find((e) => e.id === selectedEdgeId) || null,
    [edges, selectedEdgeId]
  );

  const groupBounds = useMemo(() => computeGroupBounds(nodes), [nodes]);

  // 节点 ID → 节点（边锚点计算用） */
  const nodeMap = useMemo(() => {
    const m = new Map<string, WorkflowNode>();
    nodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [nodes]);

  // ============================================
  // 渲染
  // ============================================

  return (
    <div
      className="relative flex h-full w-full gap-2 overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] p-2 backdrop-blur-md"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* ===== 左侧栏：节点面板 + 草稿列表 ===== */}
      <aside className="flex w-1/5 min-w-[180px] max-w-[260px] flex-col gap-2 overflow-hidden">
        <NodePalette
          disabled={!currentDraftId}
          onTypeMouseDown={handleTypeCardMouseDown}
        />
        <DraftList
          drafts={drafts}
          currentDraftId={currentDraftId}
          dirty={dirty}
          onSelect={handleSelectDraft}
          onCreate={handleCreateDraft}
          onDelete={handleDeleteDraft}
          onRename={handleRenameDraft}
        />
      </aside>

      {/* ===== 中部：工具栏 + 画布 ===== */}
      <section className="flex min-w-0 flex-1 flex-col gap-2">
        <Toolbar
          canUndo={undoStack.length > 0}
          canRedo={redoStack.length > 0}
          canSave={!!currentDraftId && dirty}
          canTest={!!selectedNode}
          testing={testing}
          hasDraft={!!currentDraftId}
          version={currentDraft?.version ?? 0}
          zoom={zoom}
          onUndo={undo}
          onRedo={redo}
          onSave={handleSave}
          onTest={() => selectedNode && runNodeTest(selectedNode)}
          onToggleVersions={() => setShowVersionPanel((v) => !v)}
          onCopy={() => selectedNodeId && copyNode(selectedNodeId)}
          onPaste={pasteNode}
          canCopy={!!selectedNodeId}
          canPaste={!!clipboardNode}
          onZoomIn={() => zoomBy(1.15)}
          onZoomOut={() => zoomBy(1 / 1.15)}
          onResetView={resetView}
          draftName={currentDraft?.name ?? ""}
        />

        <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-white/[0.06] bg-[#070b14]">
          {/* 画布网格背景 */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
              backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
              backgroundPosition: `${panX}px ${panY}px`,
            }}
          />

          {/* 画布主体 */}
          <div
            ref={canvasRef}
            className="absolute inset-0 cursor-grab active:cursor-grabbing"
            onMouseDown={handleCanvasMouseDown}
          >
            <div
              className="absolute left-0 top-0 origin-top-left"
              style={{
                transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
              }}
            >
              {/* SVG 连线层 */}
              <svg
                className="absolute left-0 top-0 overflow-visible"
                width={4000}
                height={3000}
                style={{ pointerEvents: "none" }}
              >
                {/* 分组包围框 */}
                {Array.from(groupBounds.entries()).map(([group, b]) => (
                  <g key={group}>
                    <rect
                      x={b.minX - 16}
                      y={b.minY - 28}
                      width={b.maxX - b.minX + 32}
                      height={b.maxY - b.minY + 44}
                      rx={10}
                      fill="rgba(99,102,241,0.04)"
                      stroke="rgba(129,140,248,0.35)"
                      strokeWidth={1}
                      strokeDasharray="4 4"
                    />
                    <text
                      x={b.minX - 12}
                      y={b.minY - 34}
                      fill="rgba(199,210,254,0.7)"
                      fontSize={11}
                      fontFamily="monospace"
                    >
                      {group}
                    </text>
                  </g>
                ))}

                {/* 已有边 */}
                {edges.map((edge) => {
                  const from = nodeMap.get(edge.from);
                  const to = nodeMap.get(edge.to);
                  if (!from || !to) return null;
                  const s = rightAnchor(from);
                  const t = leftAnchor(to);
                  const color = NODE_TYPE_META[from.type].color;
                  const selected = edge.id === selectedEdgeId;
                  const midX = (s.x + t.x) / 2;
                  const midY = (s.y + t.y) / 2;
                  return (
                    <g key={edge.id} style={{ pointerEvents: "auto" }}>
                      <path
                        d={bezierPath(s.x, s.y, t.x, t.y)}
                        fill="none"
                        stroke={selected ? "#fff" : color}
                        strokeWidth={selected ? 2.4 : 1.8}
                        strokeOpacity={selected ? 1 : 0.7}
                        style={{ cursor: "pointer", pointerEvents: "stroke" }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setSelectedEdgeId(edge.id);
                          setSelectedNodeId(null);
                        }}
                      />
                      {/* 箭头 */}
                      <polygon
                        points={`${t.x},${t.y} ${t.x - 8},${t.y - 4} ${t.x - 8},${t.y + 4}`}
                        fill={selected ? "#fff" : color}
                        fillOpacity={selected ? 1 : 0.8}
                      />
                      {edge.label && (
                        <g style={{ pointerEvents: "auto" }}>
                          <rect
                            x={midX - 24}
                            y={midY - 9}
                            width={48}
                            height={18}
                            rx={4}
                            fill="#0b1220"
                            stroke="rgba(255,255,255,0.08)"
                          />
                          <text
                            x={midX}
                            y={midY + 3}
                            textAnchor="middle"
                            fill="#cbd5e1"
                            fontSize={10}
                            fontFamily="monospace"
                          >
                            {edge.label}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}

                {/* 临时连线（拖拽中） */}
                {tempEdge && (
                  <path
                    d={bezierPath(tempEdge.fromX, tempEdge.fromY, tempEdge.toX, tempEdge.toY)}
                    fill="none"
                    stroke={tempEdge.color}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    strokeOpacity={0.9}
                  />
                )}
              </svg>

              {/* 节点层 */}
              {nodes.map((node) => (
                <CanvasNode
                  key={node.id}
                  node={node}
                  selected={node.id === selectedNodeId}
                  editingLabel={node.id === editingLabelId}
                  onMouseDown={(e) => handleNodeMouseDown(e, node)}
                  onConnectMouseDown={(e) => handleConnectMouseDown(e, node)}
                  onContextMenu={(e) => handleNodeContextMenu(e, node)}
                  onDoubleClick={() => setEditingLabelId(node.id)}
                  onLabelChange={(label) => {
                    updateNode(node.id, { label });
                  }}
                  onLabelCommit={() => setEditingLabelId(null)}
                />
              ))}
            </div>
          </div>

          {/* 空状态 */}
          {!currentDraftId && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
              <Workflow size={40} className="text-gray-700" />
              <p className="text-sm font-mono text-gray-500">空白画布</p>
              <p className="text-xs font-mono text-gray-600">
                请在左侧创建或选择一个工作流草稿
              </p>
            </div>
          )}
          {currentDraftId && nodes.length === 0 && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
              <Workflow size={36} className="text-gray-700" />
              <p className="text-xs font-mono text-gray-600">
                从左侧拖拽节点类型到画布开始编排
              </p>
            </div>
          )}

          {/* 缩放指示 */}
          <div className="pointer-events-none absolute bottom-2 right-2 rounded bg-black/40 px-2 py-0.5 font-mono text-[10px] text-gray-400">
            {Math.round(zoom * 100)}%
          </div>

          {/* 版本面板 */}
          <AnimatePresence>
            {showVersionPanel && currentDraft && (
              <VersionPanel
                draft={currentDraft}
                onRollback={handleRollback}
                onClose={() => setShowVersionPanel(false)}
              />
            )}
          </AnimatePresence>

          {/* 测试结果面板 */}
          <AnimatePresence>
            {(testing || testResult) && selectedNode && (
              <TestResultPanel
                testing={testing}
                result={testResult}
                onClose={() => setTestResult(null)}
              />
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ===== 右侧栏：配置面板 ===== */}
      <aside className="flex w-1/5 min-w-[200px] max-w-[280px] flex-col overflow-hidden rounded-lg border border-white/[0.06] bg-white/[0.02]">
        <ConfigPanel
          key={selectedNodeId || selectedEdgeId || "empty"}
          node={selectedNode}
          edge={selectedEdge}
          onUpdateNode={updateNode}
          onUpdateConfig={updateNodeConfig}
          onUpdateEdgeLabel={updateEdgeLabel}
          onFocusField={(key) => {
            if (editingFieldRef.current !== key) {
              pushHistory();
              editingFieldRef.current = key;
            }
          }}
          onBlurField={(key) => {
            if (editingFieldRef.current === key) editingFieldRef.current = null;
          }}
          onTest={runNodeTest}
          testing={testing}
        />
      </aside>

      {/* 拖拽幽灵 */}
      {ghost && <DragGhost type={ghost.type} x={ghost.x} y={ghost.y} />}

      {/* 右键菜单 */}
      <AnimatePresence>
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            nodeId={contextMenu.nodeId}
            onClose={() => setContextMenu(null)}
            onCopy={(id) => {
              copyNode(id);
              setContextMenu(null);
            }}
            onDelete={(id) => {
              deleteNode(id);
              setContextMenu(null);
            }}
            onGroup={(id) => {
              const n = nodesRef.current.find((x) => x.id === id);
              const g = window.prompt("输入分组名称（留空清除分组）", n?.group || "");
              if (g !== null) setNodeGroup(id, g.trim());
              setContextMenu(null);
            }}
            onNote={(id) => {
              const n = nodesRef.current.find((x) => x.id === id);
              const note = window.prompt("输入注释（留空清除）", n?.note || "");
              if (note !== null) updateNode(id, { note: note || undefined });
              setContextMenu(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：左侧节点面板
// ============================================

interface NodePaletteProps {
  disabled: boolean;
  onTypeMouseDown: (e: ReactMouseEvent, type: NodeType) => void;
}

function NodePalette({ disabled, onTypeMouseDown }: NodePaletteProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
      <div className="mb-2 flex items-center gap-1.5 px-1">
        <Layers size={12} className="text-indigo-400" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
          节点类型
        </span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-0.5">
        {NODE_TYPE_ORDER.map((type) => {
          const meta = NODE_TYPE_META[type];
          const Icon = meta.icon;
          return (
            <div
              key={type}
              onMouseDown={(e) => !disabled && onTypeMouseDown(e, type)}
              className={`group flex cursor-grab items-center gap-2 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 transition-all ${
                disabled
                  ? "cursor-not-allowed opacity-40"
                  : "hover:border-white/10 hover:bg-white/[0.05] active:cursor-grabbing"
              }`}
              style={{ borderLeft: `2px solid ${meta.color}` }}
              title={disabled ? "请先创建或选择草稿" : `拖拽到画布创建「${meta.label}」`}
            >
              <div
                className="flex h-6 w-6 items-center justify-center rounded"
                style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}33` }}
              >
                <Icon size={12} style={{ color: meta.color }} />
              </div>
              <span className="text-[11px] font-mono text-gray-300 group-hover:text-white">
                {meta.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 子组件：草稿列表
// ============================================

interface DraftListProps {
  drafts: WorkflowDraft[];
  currentDraftId: string | null;
  dirty: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

function DraftList({
  drafts,
  currentDraftId,
  dirty,
  onSelect,
  onCreate,
  onDelete,
  onRename,
}: DraftListProps) {
  return (
    <div className="flex max-h-[40%] flex-col rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
      <div className="mb-2 flex items-center justify-between px-1">
        <div className="flex items-center gap-1.5">
          <FileText size={12} className="text-emerald-400" />
          <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
            草稿列表
          </span>
        </div>
        <button
          onClick={onCreate}
          className="flex items-center gap-1 rounded border border-white/[0.06] bg-white/[0.02] px-1.5 py-0.5 text-[10px] font-mono text-gray-300 transition-colors hover:border-emerald-500/40 hover:text-emerald-300"
          title="新建工作流草稿"
        >
          <Plus size={10} /> 新建
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-0.5">
        {drafts.length === 0 && (
          <div className="px-2 py-3 text-center text-[10px] font-mono text-gray-600">
            暂无草稿
          </div>
        )}
        {drafts.map((d) => {
          const active = d.id === currentDraftId;
          return (
            <div
              key={d.id}
              onClick={() => onSelect(d.id)}
              className={`group flex cursor-pointer items-center gap-1.5 rounded border px-2 py-1.5 transition-all ${
                active
                  ? "border-emerald-500/40 bg-emerald-500/10"
                  : "border-white/[0.06] bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.04]"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="truncate text-[11px] font-mono text-gray-200">
                    {d.name}
                  </span>
                  {active && dirty && (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400"
                      title="有未保存的更改"
                    />
                  )}
                </div>
                <div className="text-[9px] font-mono text-gray-600">
                  v{d.version} · {d.nodes.length} 节点
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const name = window.prompt("重命名草稿", d.name);
                    if (name && name.trim()) onRename(d.id, name.trim());
                  }}
                  className="rounded p-0.5 text-gray-500 hover:bg-white/10 hover:text-white"
                  title="重命名"
                >
                  <Pencil size={10} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`删除草稿「${d.name}」？`)) onDelete(d.id);
                  }}
                  className="rounded p-0.5 text-gray-500 hover:bg-red-500/20 hover:text-red-400"
                  title="删除"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// 子组件：顶部工具栏
// ============================================

interface ToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  canSave: boolean;
  canTest: boolean;
  canCopy: boolean;
  canPaste: boolean;
  hasDraft: boolean;
  testing: boolean;
  version: number;
  zoom: number;
  draftName: string;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onTest: () => void;
  onToggleVersions: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
}

function Toolbar(props: ToolbarProps) {
  const btn =
    "flex items-center justify-center rounded-md border border-white/[0.06] bg-white/[0.02] h-7 w-7 text-gray-300 transition-colors hover:border-white/10 hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-white/[0.06] disabled:hover:bg-white/[0.02] disabled:hover:text-gray-300";
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.02] px-2 py-1.5">
      {/* 草稿名称 */}
      <div className="flex min-w-0 items-center gap-1.5 px-1">
        <Workflow size={13} className="shrink-0 text-indigo-400" />
        <span className="truncate text-[11px] font-mono text-gray-300" title={props.draftName}>
          {props.draftName || "未选择草稿"}
        </span>
        {props.hasDraft && (
          <span className="shrink-0 rounded bg-white/[0.06] px-1 py-0.5 text-[9px] font-mono text-gray-400">
            v{props.version}
          </span>
        )}
      </div>

      <div className="h-5 w-px bg-white/[0.06]" />

      {/* 撤销/重做 */}
      <button className={btn} disabled={!props.canUndo} onClick={props.onUndo} title="撤销 (Ctrl+Z)">
        <Undo2 size={13} />
      </button>
      <button className={btn} disabled={!props.canRedo} onClick={props.onRedo} title="重做 (Ctrl+Y)">
        <Redo2 size={13} />
      </button>

      <div className="h-5 w-px bg-white/[0.06]" />

      {/* 复制/粘贴 */}
      <button className={btn} disabled={!props.canCopy} onClick={props.onCopy} title="复制 (Ctrl+C)">
        <Copy size={13} />
      </button>
      <button className={btn} disabled={!props.canPaste} onClick={props.onPaste} title="粘贴 (Ctrl+V)">
        <ClipboardPaste size={13} />
      </button>

      <div className="h-5 w-px bg-white/[0.06]" />

      {/* 保存/测试/版本 */}
      <button
        className={`${btn} w-auto px-2 ${props.canSave ? "border-emerald-500/40 text-emerald-300" : ""}`}
        disabled={!props.canSave}
        onClick={props.onSave}
        title="保存草稿并生成版本"
      >
        <Save size={13} />
        <span className="ml-1 text-[10px] font-mono">保存</span>
      </button>
      <button
        className={`${btn} w-auto px-2 ${props.canTest ? "border-amber-500/40 text-amber-300" : ""}`}
        disabled={!props.canTest || props.testing}
        onClick={props.onTest}
        title="测试运行选中节点"
      >
        {props.testing ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
        <span className="ml-1 text-[10px] font-mono">{props.testing ? "测试中" : "测试"}</span>
      </button>
      <button
        className={btn}
        disabled={!props.hasDraft}
        onClick={props.onToggleVersions}
        title="版本管理与回滚"
      >
        <History size={13} />
      </button>

      <div className="ml-auto flex items-center gap-1">
        <button className={btn} onClick={props.onZoomOut} title="缩小">
          <ZoomOut size={13} />
        </button>
        <span className="w-10 text-center text-[10px] font-mono text-gray-400">
          {Math.round(props.zoom * 100)}%
        </span>
        <button className={btn} onClick={props.onZoomIn} title="放大">
          <ZoomIn size={13} />
        </button>
        <button className={btn} onClick={props.onResetView} title="重置视图">
          <Maximize2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ============================================
// 子组件：画布节点
// ============================================

interface CanvasNodeProps {
  node: WorkflowNode;
  selected: boolean;
  editingLabel: boolean;
  onMouseDown: (e: ReactMouseEvent) => void;
  onConnectMouseDown: (e: ReactMouseEvent) => void;
  onContextMenu: (e: ReactMouseEvent) => void;
  onDoubleClick: () => void;
  onLabelChange: (label: string) => void;
  onLabelCommit: () => void;
}

function CanvasNode({
  node,
  selected,
  editingLabel,
  onMouseDown,
  onConnectMouseDown,
  onContextMenu,
  onDoubleClick,
  onLabelChange,
  onLabelCommit,
}: CanvasNodeProps) {
  const meta = NODE_TYPE_META[node.type];
  const Icon = meta.icon;
  const style: CSSProperties = {
    left: node.x,
    top: node.y,
    width: NODE_W,
    height: NODE_H,
  };
  return (
    <div
      data-node-id={node.id}
      onMouseDown={onMouseDown}
      onContextMenu={onContextMenu}
      onDoubleClick={onDoubleClick}
      className={`absolute flex cursor-grab items-center gap-2 rounded-lg border px-2.5 backdrop-blur-sm transition-shadow active:cursor-grabbing ${
        selected
          ? "border-white/40 bg-white/[0.06] shadow-[0_0_0_1px_rgba(255,255,255,0.15),0_4px_20px_rgba(0,0,0,0.4)]"
          : "border-white/[0.06] bg-white/[0.03] hover:border-white/15"
      }`}
      style={style}
    >
      {/* 左侧色条 */}
      <div
        className="absolute left-0 top-0 h-full w-1 rounded-l-lg"
        style={{ background: meta.color }}
      />
      {/* 图标 */}
      <div
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded"
        style={{ background: `${meta.color}1a`, border: `1px solid ${meta.color}40` }}
      >
        <Icon size={14} style={{ color: meta.color }} />
      </div>
      {/* 标签 */}
      <div className="min-w-0 flex-1">
        {editingLabel ? (
          <input
            autoFocus
            defaultValue={node.label}
            onBlur={(e) => {
              onLabelChange(e.target.value);
              onLabelCommit();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onLabelChange((e.target as HTMLInputElement).value);
                onLabelCommit();
              }
              if (e.key === "Escape") onLabelCommit();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-full rounded bg-black/40 px-1 py-0.5 text-[11px] font-mono text-white outline-none ring-1 ring-white/30"
          />
        ) : (
          <>
            <div className="truncate text-[11px] font-mono text-gray-100">{node.label}</div>
            <div className="text-[9px] font-mono uppercase tracking-wider text-gray-500">
              {meta.label}
            </div>
          </>
        )}
      </div>
      {/* 注释指示 */}
      {node.note && (
        <div title={node.note} className="shrink-0">
          <MessageSquare size={11} className="text-amber-400/80" />
        </div>
      )}
      {/* 连接点（右侧） */}
      <div
        onMouseDown={onConnectMouseDown}
        className="absolute -right-2 top-1/2 flex h-3 w-3 -translate-y-1/2 cursor-crosshair items-center justify-center rounded-full border bg-[#0b1220] transition-transform hover:scale-125"
        style={{ borderColor: meta.color }}
        title="拖拽到其他节点创建连线"
      >
        <div className="h-1 w-1 rounded-full" style={{ background: meta.color }} />
      </div>
      {/* 分组标记 */}
      {node.group && (
        <div
          className="absolute -top-2 left-2 rounded px-1 text-[8px] font-mono"
          style={{ background: "#1e1b4b", color: "#c7d2fe", border: "1px solid rgba(129,140,248,0.3)" }}
        >
          {node.group}
        </div>
      )}
    </div>
  );
}

// ============================================
// 子组件：右侧配置面板
// ============================================

interface ConfigPanelProps {
  node: WorkflowNode | null;
  edge: WorkflowEdge | null;
  onUpdateNode: (id: string, patch: Partial<WorkflowNode>) => void;
  onUpdateConfig: (id: string, patch: Partial<WorkflowNodeConfig>) => void;
  onUpdateEdgeLabel: (id: string, label: string) => void;
  onFocusField: (key: string) => void;
  onBlurField: (key: string) => void;
  onTest: (node: WorkflowNode) => void;
  testing: boolean;
}

function ConfigPanel({
  node,
  edge,
  onUpdateNode,
  onUpdateConfig,
  onUpdateEdgeLabel,
  onFocusField,
  onBlurField,
  onTest,
  testing,
}: ConfigPanelProps) {
  // 边配置
  if (!node && edge) {
    return (
      <div className="flex h-full flex-col">
        <PanelHeader icon={Settings} title="连线配置" color="#94a3b8" />
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2.5">
          <Field label="连线标签">
            <input
              defaultValue={edge.label || ""}
              onFocus={() => onFocusField(`edge-${edge.id}-label`)}
              onBlur={(e) => {
                onUpdateEdgeLabel(edge.id, e.target.value);
                onBlurField(`edge-${edge.id}-label`);
              }}
              placeholder="如：true / 默认分支"
              className="wfe-input"
            />
          </Field>
          <p className="text-[10px] font-mono text-gray-600">
            提示：条件分支节点的出边可标注分支条件。
          </p>
        </div>
      </div>
    );
  }

  // 节点配置
  if (node) {
    const meta = NODE_TYPE_META[node.type];
    const Icon = meta.icon;
    return (
      <div className="flex h-full flex-col">
        <PanelHeader icon={Icon} title={meta.label} color={meta.color} />

        <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-2.5">
          {/* 通用：标签 */}
          <Field label="节点标签">
            <input
              defaultValue={node.label}
              onFocus={() => onFocusField(`label`)}
              onBlur={(e) => {
                onUpdateNode(node.id, { label: e.target.value });
                onBlurField(`label`);
              }}
              className="wfe-input"
            />
          </Field>

          {/* 分组 / 注释 */}
          <Field label="分组">
            <input
              key={`grp-${node.group || ""}`}
              defaultValue={node.group || ""}
              onFocus={() => onFocusField(`group`)}
              onBlur={(e) => {
                onUpdateNode(node.id, { group: e.target.value || undefined });
                onBlurField(`group`);
              }}
              placeholder="输入分组名"
              className="wfe-input"
            />
          </Field>
          <Field label="注释">
            <textarea
              key={`note-${node.note || ""}`}
              defaultValue={node.note || ""}
              onFocus={() => onFocusField(`note`)}
              onBlur={(e) => {
                onUpdateNode(node.id, { note: e.target.value || undefined });
                onBlurField(`note`);
              }}
              rows={2}
              placeholder="节点备注..."
              className="wfe-input resize-none"
            />
          </Field>

          <div className="my-1 h-px bg-white/[0.06]" />

          {/* 按类型渲染配置 */}
          <TypeSpecificConfig
            node={node}
            onUpdateConfig={onUpdateConfig}
            onFocusField={onFocusField}
            onBlurField={onBlurField}
          />

          {/* 测试按钮 */}
          <button
            onClick={() => onTest(node)}
            disabled={testing}
            className="mt-2 flex items-center justify-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 py-1.5 text-[11px] font-mono text-amber-300 transition-colors hover:bg-amber-500/20 disabled:opacity-50"
          >
            {testing ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {testing ? "测试中..." : "测试运行此节点"}
          </button>
        </div>
      </div>
    );
  }

  // 空状态
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
      <Settings size={28} className="text-gray-700" />
      <p className="text-[11px] font-mono text-gray-500">未选中节点</p>
      <p className="text-[10px] font-mono text-gray-600">
        点击画布节点查看并编辑配置
      </p>
    </div>
  );
}

// ============================================
// 子组件：按节点类型的差异化配置
// ============================================

interface TypeSpecificConfigProps {
  node: WorkflowNode;
  onUpdateConfig: (id: string, patch: Partial<WorkflowNodeConfig>) => void;
  onFocusField: (key: string) => void;
  onBlurField: (key: string) => void;
}

function TypeSpecificConfig({
  node,
  onUpdateConfig,
  onFocusField,
  onBlurField,
}: TypeSpecificConfigProps) {
  const c = node.config;

  // ---- AI 调用 ----
  if (node.type === "ai_call") {
    return (
      <>
        <Field label="运行时">
          <select
            value={c.runtime || "doubao"}
            onChange={(e) => onUpdateConfig(node.id, { runtime: e.target.value })}
            className="wfe-input"
          >
            <option value="doubao">豆包</option>
            <option value="openai">OpenAI</option>
            <option value="claude">Claude</option>
            <option value="local">本地模型</option>
          </select>
        </Field>
        <Field label="动作 (action)">
          <input
            defaultValue={c.action || ""}
            onFocus={() => onFocusField("ai_action")}
            onBlur={(e) => {
              onUpdateConfig(node.id, { action: e.target.value });
              onBlurField("ai_action");
            }}
            placeholder="如：summarize / extract"
            className="wfe-input"
          />
        </Field>
        <Field label="参数 (JSON)">
          <ParamsEditor
            value={c.params}
            onFocus={() => onFocusField("ai_params")}
            onBlur={(val) => {
              onUpdateConfig(node.id, { params: val });
              onBlurField("ai_params");
            }}
          />
        </Field>
      </>
    );
  }

  // ---- 数据处理 ----
  if (node.type === "data_process") {
    return (
      <>
        <Field label="动作">
          <select
            value={c.action || "transform"}
            onChange={(e) => onUpdateConfig(node.id, { action: e.target.value })}
            className="wfe-input"
          >
            <option value="transform">转换</option>
            <option value="filter">过滤</option>
            <option value="aggregate">聚合</option>
            <option value="join">关联</option>
          </select>
        </Field>
        <Field label="参数 (JSON)">
          <ParamsEditor
            value={c.params}
            onFocus={() => onFocusField("dp_params")}
            onBlur={(val) => {
              onUpdateConfig(node.id, { params: val });
              onBlurField("dp_params");
            }}
          />
        </Field>
      </>
    );
  }

  // ---- 条件分支 ----
  if (node.type === "condition") {
    const conds = c.conditions || [];
    return (
      <>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
            条件列表
          </span>
          <button
            onClick={() =>
              onUpdateConfig(node.id, {
                conditions: [
                  ...conds,
                  { field: "", operator: "==", value: "" },
                ],
              })
            }
            className="flex items-center gap-0.5 rounded border border-white/[0.06] px-1 py-0.5 text-[10px] font-mono text-gray-300 hover:border-white/10 hover:text-white"
          >
            <Plus size={9} /> 添加
          </button>
        </div>
        {conds.map((cond, i) => (
          <div
            key={i}
            className="flex flex-col gap-1 rounded border border-white/[0.06] bg-white/[0.02] p-1.5"
          >
            <input
              defaultValue={cond.field}
              onFocus={() => onFocusField(`cond_field_${i}`)}
              onBlur={(e) => {
                const next = conds.map((x, j) =>
                  j === i ? { ...x, field: e.target.value } : x
                );
                onUpdateConfig(node.id, { conditions: next });
                onBlurField(`cond_field_${i}`);
              }}
              placeholder="字段名"
              className="wfe-input"
            />
            <div className="flex gap-1">
              <select
                value={cond.operator}
                onChange={(e) => {
                  const next = conds.map((x, j) =>
                    j === i ? { ...x, operator: e.target.value } : x
                  );
                  onUpdateConfig(node.id, { conditions: next });
                }}
                className="wfe-input w-20"
              >
                {CONDITION_OPERATORS.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
              <input
                defaultValue={cond.value}
                onFocus={() => onFocusField(`cond_value_${i}`)}
                onBlur={(e) => {
                  const next = conds.map((x, j) =>
                    j === i ? { ...x, value: e.target.value } : x
                  );
                  onUpdateConfig(node.id, { conditions: next });
                  onBlurField(`cond_value_${i}`);
                }}
                placeholder="值"
                className="wfe-input flex-1"
              />
            </div>
            <button
              onClick={() =>
                onUpdateConfig(node.id, {
                  conditions: conds.filter((_, j) => j !== i),
                })
              }
              className="self-end rounded p-0.5 text-gray-500 hover:text-red-400"
            >
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </>
    );
  }

  // ---- 循环 ----
  if (node.type === "loop") {
    const maxIter = (c.params as { maxIterations?: number })?.maxIterations;
    return (
      <>
        <Field label="动作">
          <select
            value={c.action || "iterate"}
            onChange={(e) => onUpdateConfig(node.id, { action: e.target.value })}
            className="wfe-input"
          >
            <option value="iterate">遍历集合</option>
            <option value="repeat">重复 N 次</option>
            <option value="while">while 条件</option>
          </select>
        </Field>
        <Field label="最大迭代次数">
          <input
            type="number"
            defaultValue={maxIter ?? 10}
            onFocus={() => onFocusField("loop_max")}
            onBlur={(e) => {
              onUpdateConfig(node.id, {
                params: { ...c.params, maxIterations: Number(e.target.value) || 0 },
              });
              onBlurField("loop_max");
            }}
            className="wfe-input"
          />
        </Field>
      </>
    );
  }

  // ---- 人工审批 ----
  if (node.type === "approval") {
    return (
      <>
        <Field label="审批策略">
          <select
            value={c.approvalType || "any"}
            onChange={(e) =>
              onUpdateConfig(node.id, {
                approvalType: e.target.value as WorkflowNodeConfig["approvalType"],
              })
            }
            className="wfe-input"
          >
            {APPROVAL_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="审批人（逗号分隔）">
          <input
            defaultValue={(c.approvers || []).join(", ")}
            onFocus={() => onFocusField("approvers")}
            onBlur={(e) => {
              onUpdateConfig(node.id, {
                approvers: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              });
              onBlurField("approvers");
            }}
            placeholder="如：张三, 李四"
            className="wfe-input"
          />
        </Field>
      </>
    );
  }

  // ---- 通知 ----
  if (node.type === "notification") {
    return (
      <>
        <Field label="渠道">
          <select
            value={c.channel || "im"}
            onChange={(e) => onUpdateConfig(node.id, { channel: e.target.value })}
            className="wfe-input"
          >
            {NOTIFY_CHANNELS.map((ch) => (
              <option key={ch.value} value={ch.value}>
                {ch.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="消息内容">
          <textarea
            key={`msg-${c.message || ""}`}
            defaultValue={c.message || ""}
            onFocus={() => onFocusField("notify_msg")}
            onBlur={(e) => {
              onUpdateConfig(node.id, { message: e.target.value });
              onBlurField("notify_msg");
            }}
            rows={3}
            placeholder="通知正文..."
            className="wfe-input resize-none"
          />
        </Field>
      </>
    );
  }

  // ---- HTTP 请求 ----
  if (node.type === "http") {
    return (
      <>
        <Field label="方法">
          <select
            value={c.method || "GET"}
            onChange={(e) => onUpdateConfig(node.id, { method: e.target.value })}
            className="wfe-input"
          >
            {HTTP_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </Field>
        <Field label="URL">
          <input
            defaultValue={c.url || ""}
            onFocus={() => onFocusField("http_url")}
            onBlur={(e) => {
              onUpdateConfig(node.id, { url: e.target.value });
              onBlurField("http_url");
            }}
            placeholder="https://api.example.com/v1/..."
            className="wfe-input"
          />
        </Field>
        <Field label="请求参数 (JSON)">
          <ParamsEditor
            value={c.params}
            onFocus={() => onFocusField("http_params")}
            onBlur={(val) => {
              onUpdateConfig(node.id, { params: val });
              onBlurField("http_params");
            }}
          />
        </Field>
      </>
    );
  }

  // ---- 等待 ----
  if (node.type === "wait") {
    return (
      <Field label="等待时长（秒）">
        <input
          type="number"
          defaultValue={c.duration ?? 60}
          onFocus={() => onFocusField("wait_dur")}
          onBlur={(e) => {
            onUpdateConfig(node.id, { duration: Number(e.target.value) || 0 });
            onBlurField("wait_dur");
          }}
          className="wfe-input"
        />
      </Field>
    );
  }

  // ---- 脚本 ----
  if (node.type === "script") {
    return (
      <Field label="脚本代码">
        <textarea
          key={`script-${c.script || ""}`}
          defaultValue={c.script || ""}
          onFocus={() => onFocusField("script")}
          onBlur={(e) => {
            onUpdateConfig(node.id, { script: e.target.value });
            onBlurField("script");
          }}
          rows={8}
          placeholder="// 在此编写脚本&#10;return { ok: true };"
          className="wfe-input resize-none font-mono text-[11px] leading-relaxed"
        />
      </Field>
    );
  }

  return null;
}

// ============================================
// 子组件：JSON 参数编辑器
// ============================================

interface ParamsEditorProps {
  value: Record<string, unknown> | undefined;
  onFocus: () => void;
  onBlur: (val: Record<string, unknown> | undefined) => void;
}

function ParamsEditor({ value, onFocus, onBlur }: ParamsEditorProps) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  // 同步外部值到文本（仅在 value 引用变化时）
  useEffect(() => {
    setText(value ? JSON.stringify(value, null, 2) : "{}");
    setError(null);
  }, [value]);

  return (
    <div className="flex flex-col gap-1">
      <textarea
        value={text}
        onFocus={onFocus}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        onBlur={() => {
          try {
            const parsed = text.trim() ? JSON.parse(text) : undefined;
            onBlur(parsed);
            setError(null);
          } catch (err) {
            setError("JSON 格式错误，已忽略更新");
            onBlur(value);
          }
        }}
        rows={4}
        className="wfe-input resize-none font-mono text-[10px] leading-relaxed"
      />
      {error && <span className="text-[9px] font-mono text-red-400">{error}</span>}
    </div>
  );
}

// ============================================
// 子组件：面板头部 / 字段容器 / 输入样式
// ============================================

function PanelHeader({
  icon: Icon,
  title,
  color,
}: {
  icon: LucideIcon;
  title: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-white/[0.06] px-3 py-2">
      <div
        className="flex h-6 w-6 items-center justify-center rounded"
        style={{ background: `${color}1a`, border: `1px solid ${color}40` }}
      >
        <Icon size={12} style={{ color }} />
      </div>
      <span className="text-[11px] font-mono uppercase tracking-wider text-gray-200">
        {title}
      </span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
        {label}
      </span>
      {children}
    </label>
  );
}

// 输入框统一样式（运行时由主组件注入一次，使用专属类名 .wfe-input 避免与全局样式冲突）
const WFE_INPUT_CSS = `
  .wfe-input {
    width: 100%;
    border-radius: 6px;
    border: 1px solid rgba(255,255,255,0.06);
    background: rgba(0,0,0,0.3);
    padding: 4px 8px;
    font-size: 11px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    color: #e5e7eb;
    outline: none;
    transition: border-color .15s, background .15s;
  }
  .wfe-input:focus { border-color: rgba(255,255,255,0.2); background: rgba(0,0,0,0.5); }
  .wfe-input::placeholder { color: #4b5563; }
`;

// ============================================
// 子组件：拖拽幽灵
// ============================================

function DragGhost({ type, x, y }: { type: NodeType; x: number; y: number }) {
  const meta = NODE_TYPE_META[type];
  const Icon = meta.icon;
  return (
    <div
      className="pointer-events-none fixed z-50 flex items-center gap-2 rounded-lg border px-2.5 py-2 opacity-90"
      style={{
        left: x,
        top: y,
        transform: "translate(-50%, -50%)",
        borderColor: meta.color,
        background: "rgba(11,18,32,0.9)",
        boxShadow: `0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px ${meta.color}40`,
      }}
    >
      <div
        className="flex h-6 w-6 items-center justify-center rounded"
        style={{ background: `${meta.color}1a`, border: `1px solid ${meta.color}40` }}
      >
        <Icon size={12} style={{ color: meta.color }} />
      </div>
      <span className="text-[11px] font-mono text-gray-100">{meta.label}</span>
    </div>
  );
}

// ============================================
// 子组件：右键菜单
// ============================================

interface ContextMenuProps {
  x: number;
  y: number;
  nodeId: string;
  onClose: () => void;
  onCopy: (id: string) => void;
  onDelete: (id: string) => void;
  onGroup: (id: string) => void;
  onNote: (id: string) => void;
}

function ContextMenu({ x, y, nodeId, onClose, onCopy, onDelete, onGroup, onNote }: ContextMenuProps) {
  // 点击外部关闭
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener("click", close);
    window.addEventListener("contextmenu", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("contextmenu", close);
    };
  }, [onClose]);

  const items: { icon: LucideIcon; label: string; color?: string; onClick: () => void }[] = [
    { icon: Copy, label: "复制", onClick: () => onCopy(nodeId) },
    { icon: Folder, label: "设置分组", onClick: () => onGroup(nodeId) },
    { icon: MessageSquare, label: "编辑注释", onClick: () => onNote(nodeId) },
    { icon: Trash2, label: "删除", color: "#f87171", onClick: () => onDelete(nodeId) },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.12 }}
      className="fixed z-50 min-w-[140px] overflow-hidden rounded-lg border border-white/10 bg-[#0b1220]/95 py-1 shadow-2xl backdrop-blur-md"
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <button
            key={i}
            onClick={it.onClick}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] font-mono text-gray-300 transition-colors hover:bg-white/5"
          >
            <Icon size={12} style={{ color: it.color || "#9ca3af" }} />
            <span style={{ color: it.color }}>{it.label}</span>
          </button>
        );
      })}
    </motion.div>
  );
}

// ============================================
// 子组件：版本面板
// ============================================

function VersionPanel({
  draft,
  onRollback,
  onClose,
}: {
  draft: WorkflowDraft;
  onRollback: (version: number) => void;
  onClose: () => void;
}) {
  const sorted = [...draft.history].sort((a, b) => b.version - a.version);
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="absolute right-2 top-2 z-20 w-64 overflow-hidden rounded-lg border border-white/10 bg-[#0b1220]/95 shadow-2xl backdrop-blur-md"
    >
      <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
        <div className="flex items-center gap-1.5">
          <History size={12} className="text-indigo-400" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-gray-300">
            版本历史
          </span>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white">
          <X size={12} />
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto py-1">
        {sorted.map((h) => {
          const isCurrent = h.version === draft.version;
          let count = "";
          try {
            const p = JSON.parse(h.snapshot) as { nodes: unknown[] };
            count = `${p.nodes.length} 节点`;
          } catch {
            count = "—";
          }
          return (
            <button
              key={h.version}
              onClick={() => !isCurrent && onRollback(h.version)}
              disabled={isCurrent}
              className={`flex w-full items-center justify-between px-3 py-1.5 text-left transition-colors ${
                isCurrent
                  ? "cursor-default bg-emerald-500/10"
                  : "hover:bg-white/5"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-mono text-gray-300">
                  v{h.version}
                </span>
                {isCurrent && (
                  <span className="text-[9px] font-mono text-emerald-400">当前</span>
                )}
              </div>
              <div className="text-right">
                <div className="text-[9px] font-mono text-gray-500">{count}</div>
                <div className="text-[9px] font-mono text-gray-600">
                  {h.timestamp.slice(5, 16).replace("T", " ")}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="border-t border-white/[0.06] px-3 py-1.5 text-[9px] font-mono text-gray-600">
        点击历史版本可回滚（不影响已保存版本）
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：测试结果面板
// ============================================

function TestResultPanel({
  testing,
  result,
  onClose,
}: {
  testing: boolean;
  result: TestResult | null;
  onClose: () => void;
}) {
  const statusMeta = {
    success: { color: "#10B981", icon: CheckCircle2, label: "通过" },
    warning: { color: "#F59E0B", icon: AlertCircle, label: "警告" },
    error: { color: "#EF4444", icon: AlertCircle, label: "失败" },
  } as const;
  const meta = result ? statusMeta[result.status] : statusMeta.success;
  const Icon = meta.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 8 }}
      transition={{ duration: 0.2 }}
      className="absolute bottom-2 left-2 z-20 w-72 overflow-hidden rounded-lg border border-white/10 bg-[#0b1220]/95 shadow-2xl backdrop-blur-md"
    >
      <div className="flex items-center justify-between border-b border-white/[0.06] px-3 py-2">
        <div className="flex items-center gap-1.5">
          {testing ? (
            <Loader2 size={12} className="animate-spin text-amber-400" />
          ) : (
            <Icon size={12} style={{ color: meta.color }} />
          )}
          <span className="text-[11px] font-mono uppercase tracking-wider text-gray-300">
            {testing ? "测试运行中" : "测试结果"}
          </span>
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white">
          <X size={12} />
        </button>
      </div>
      <div className="max-h-60 overflow-y-auto px-3 py-2">
        {testing && (
          <p className="text-[10px] font-mono text-gray-500">正在模拟执行节点...</p>
        )}
        {!testing && result && (
          <>
            <div className="mb-2 flex items-center gap-2">
              <span
                className="rounded px-1.5 py-0.5 text-[10px] font-mono"
                style={{ background: `${meta.color}1a`, color: meta.color }}
              >
                {meta.label}
              </span>
              <span className="text-[10px] font-mono text-gray-500">
                耗时 {result.durationMs}ms
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              {result.logs.map((log, i) => (
                <div
                  key={i}
                  className="font-mono text-[10px] leading-relaxed text-gray-400"
                >
                  {log}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
